import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { db, auth, loginWithGoogle, logout, handleFirestoreError, OperationType, handleRedirectLoginResult } from '../firebase';
import { collection, addDoc, onSnapshot, query, where, orderBy, serverTimestamp, getDoc, doc } from 'firebase/firestore';
import { onAuthStateChanged, User } from 'firebase/auth';
import { apiBaseUrl, buildApiUrl } from '../config/apiBaseUrl';
import type { EstimateItemType } from '../domain/estimate.types';
import {
  DEFAULT_CONTRACTOR_FINANCIAL_SETTINGS,
  normalizeContractorFinancialSettings,
  type MaterialSide,
  type PurchasePriceMode,
  type SalesVatMode,
} from '../domain/contractorFinancialSettings';
import { R1_RELEASE_FLAGS } from '../config/releaseFlags';
import { buildQuickProjectUpdate, type QuickProjectUpdate } from '../domain/quickProjectUpdate';
import { isUsableQuickProjectSnapshot } from '../domain/quickEstimateSnapshot';
import { persistQuickProject } from '../domain/quickProjectPersistence';
import {
  configureProductAnalytics,
  createPilotAnalyticsRecord,
  isPilotFunnelEvent,
} from '../lib/productAnalytics';

// Fallback market prices when Firebase is unavailable or empty
const FALLBACK_MARKET_PRICES: Record<string, number> = {
  'gruntowanie': 8,
  'malowanie': 25,
  'szpachlowanie': 37,
  'elewacja': 95,
  'tapeta': 45,
  'plytGips': 50,
  'izolacja': 40,
  'renowacja': 65,
  'hydroizolacja': 35,
  'plytki': 140,
  'silikon': 28,
  'fuga': 18,
  'klej': 16,
};

const AI_API_URL = apiBaseUrl;

export interface Discount {
  id: string;
  wholesaler: string;
  category: string;
  percentage: number;
}

export interface MaterialItem {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  basePrice: number;
  category: string;
  description?: string;
}

export interface LaborItem {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  itemType?: EstimateItemType;
  source?: 'engine' | 'manual' | 'margin_shield';
  rbgPerUnit?: number;
  priority?: 'low' | 'medium' | 'high';
  deadline?: string;
  isCompleted?: boolean;
}

export interface ScheduleDay {
  day: number;
  tasks: string[];
}

export interface BeforeAfterPair {
  itemId: string;
  beforeUrl?: string;
  afterUrl?: string;
  comment?: string;
}

export interface Project {
  id: string;
  userId: string;
  name: string;
  clientName?: string;
  date: string;
  trade: string;
  details: string;
  materials: MaterialItem[];
  laborItems?: LaborItem[];
  totalBaseCost: number;
  totalDiscountedCost: number;
  estimatedHours?: number;
  tips?: string[];
  laborCost?: number;
  transportCost?: number;
  location?: string;
  createdAt?: number;
  updatedAt?: number;
  images?: string[];
  imageDescriptions?: string[];
  vatRate?: number;
  settlementMode?: 'no_vat' | 'vat_8' | 'vat_23';
  quickEstimateSnapshot?: unknown;
  isHighRisk?: boolean;
  auxiliaryMaterialsCost?: number;
  riskBufferCost?: number;
  deadline?: string;
  marginOfError?: number;
  area?: number;
  estimatedAreaM2?: number;
  totalArea?: number;
  projectArea?: number;
  squareMeters?: number;
  surfaceArea?: number;
  voivodeship?: string;
  status: 'draft' | 'sent' | 'accepted' | 'in_progress' | 'completed' | 'rejected' | 'cancelled';
  priceMode?: 'cheap' | 'standard' | 'premium';
  margin?: number;
  totalProfit?: number;
  totalNetto?: number;
  totalBrutto?: number;
  schedule?: ScheduleDay[];
  equipment?: any[];
  totalEquipmentCost?: number;
  clientToken?: string;
  clientAcceptedAt?: number;
  clientPhone?: string;
  sentAt?: number;
  lat?: number | null;
  lng?: number | null;
  progressPhotos?: { url: string; comment: string; timestamp: number }[];
  beforeAfterPhotos?: BeforeAfterPair[];
  missedItems?: { name: string; reason: string }[];
  risks?: { name: string; severity: 'low' | 'medium' | 'high'; description: string }[];
}

export interface UserProfile {
  userId: string;
  email: string;
  status: 'trial' | 'paid' | 'blocked';
  projectCount: number;
  customPrices?: Record<string, number>;
  companyName?: string;
  nip?: string;
  phone?: string;
  role?: 'admin' | 'tester' | 'user' | 'PRO';
  vatPayer: boolean;
  purchasePriceMode: PurchasePriceMode;
  salesVatMode: SalesVatMode;
  materialSide: MaterialSide;
}

interface AppContextType {
  user: User | null;
  isAuthReady: boolean;
  isAllowed: boolean;
  userProfile: UserProfile | null;
  marketPrices: Record<string, number>;
  login: () => Promise<void>;
  logoutUser: () => Promise<void>;
  discounts: Discount[];
  addDiscount: (discount: Omit<Discount, 'id'>) => void;
  removeDiscount: (id: string) => void;
  projects: Project[];
  addProject: (project: Omit<Project, 'id' | 'userId' | 'createdAt' | 'status'>) => Promise<string>;
  updateProject: (id: string, project: QuickProjectUpdate<Project>) => Promise<void>;
  updateProjectStatus: (id: string, status: Project['status']) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  logError: (error: string, projectDescription?: string) => Promise<void>;
  sendFeedback: (description: string, projectId?: string) => Promise<void>;
  updateUserProfile: (data: Partial<UserProfile>) => Promise<void>;
  addProgressPhoto: (projectId: string, photo: { url: string; comment: string }) => Promise<void>;
  generateAIPhotoComment: (imageUrl: string, context: string) => Promise<string>;
  getProjectByToken: (token: string) => Promise<Project | null>;
  acceptProjectAsClient: (projectId: string, token: string) => Promise<void>;
  addToGoogleCalendar: (event: { summary: string; location: string; description: string; start: string; end: string }) => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [marketPrices, setMarketPrices] = useState<Record<string, number>>({});
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    handleRedirectLoginResult().catch((error) => {
      const authError = error as { code?: unknown; message?: unknown };
      console.error('[firebase-auth] redirect result handling failed', {
        code: typeof authError.code === 'string' ? authError.code : 'unknown',
        message: typeof authError.message === 'string' ? authError.message : String(error),
      });
    });
  }, []);

  useEffect(() => {
    configureProductAnalytics((event) => {
      if (!isPilotFunnelEvent(event)) return;
      void addDoc(collection(db, 'pilot_events'), createPilotAnalyticsRecord(event)).catch((error) => {
        console.warn('[pilot-analytics] event was not saved', error);
      });
    });

    return () => configureProductAnalytics(null);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
          // Check if user is allowed
        try {
          const allowedDoc = await getDoc(doc(db, 'allowed_users', currentUser.uid));
          
          const allowed =
            R1_RELEASE_FLAGS.openPilotRegistration ||
            allowedDoc.exists() ||
            currentUser.email === 'bp7013007@gmail.com';
          setIsAllowed(allowed);

          if (allowed) {
            const role = allowedDoc.exists() ? allowedDoc.data().role : (currentUser.email === 'bp7013007@gmail.com' ? 'admin' : 'user');
            
            // Get or create profile
            const profileDoc = await getDoc(doc(db, 'user_profiles', currentUser.uid));
            if (profileDoc.exists()) {
              const data = profileDoc.data();
              const financialSettings = normalizeContractorFinancialSettings(data);
              setUserProfile({
                ...data,
                ...financialSettings,
                role,
                customPrices: data.customPrices || {}
              } as UserProfile);
              const needsFinancialSettingsMigration =
                typeof data.vatPayer !== 'boolean' ||
                !data.purchasePriceMode ||
                !data.salesVatMode ||
                !data.materialSide;
              if (needsFinancialSettingsMigration) {
                const { setDoc } = await import('firebase/firestore');
                await setDoc(
                  doc(db, 'user_profiles', currentUser.uid),
                  financialSettings,
                  { merge: true }
                );
              }
            } else {
              const newProfile: UserProfile = {
                userId: currentUser.uid,
                email: currentUser.email || '',
                status: 'trial',
                projectCount: 0,
                customPrices: {},
                role,
                ...DEFAULT_CONTRACTOR_FINANCIAL_SETTINGS,
              };
              const { setDoc } = await import('firebase/firestore');
              await setDoc(doc(db, 'user_profiles', currentUser.uid), {
                ...newProfile,
                customPrices: {}
              });
              setUserProfile(newProfile);
            }
          }
        } catch (error) {
          console.error("Error checking whitelist:", error);
          setIsAllowed(false);
        }
      } else {
        setIsAllowed(false);
        setUserProfile(null);
      }
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Fetch global market prices with error handling and fallback
    let unsubscribe: (() => void) | undefined;
    
    try {
      unsubscribe = onSnapshot(
        doc(db, 'global_settings', 'market_prices'),
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data() as Record<string, number>;
            console.log('[AppContext] Market prices loaded:', Object.keys(data).length, 'items');
            setMarketPrices(data);
          } else {
            console.warn('[AppContext] Market prices document not found, using fallback');
            setMarketPrices(FALLBACK_MARKET_PRICES);
          }
        },
        (error) => {
          console.error('[AppContext] Error loading market prices:', error);
          setMarketPrices(FALLBACK_MARKET_PRICES);
        }
      );
    } catch (error) {
      console.error('[AppContext] Failed to subscribe to market prices:', error);
      setMarketPrices(FALLBACK_MARKET_PRICES);
    }
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    async function testConnection() {
      try {
        await getDoc(doc(db, 'test', 'connection'));
      } catch (error) {
        if(error instanceof Error && error.message.includes('the client is offline')) {
          console.error("Please check your Firebase configuration.");
        }
      }
    }
    testConnection();
  }, []);

  useEffect(() => {
    if (!isAuthReady) {
      return;
    }

    if (!user) {
      setProjects([]);
      return;
    }

    const isAdmin = userProfile?.role === 'admin';
    
    const q = isAdmin 
      ? query(collection(db, 'projects'), orderBy('createdAt', 'desc'))
      : query(
          collection(db, 'projects'),
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const projectsData: Project[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        projectsData.push({
          id: doc.id,
          userId: data.userId,
          name: data.name,
          clientName: data.clientName,
          date: data.date,
          trade: data.trade,
          details: data.details,
          materials: data.materials || [],
          totalBaseCost: data.totalBaseCost,
          totalDiscountedCost: data.totalDiscountedCost,
          estimatedHours: data.estimatedHours,
          tips: data.tips || [],
          laborCost: data.laborCost,
          transportCost: data.transportCost,
          location: data.location,
          lat: data.lat ?? undefined,
          lng: data.lng ?? undefined,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          images: data.images || [],
          imageDescriptions: data.imageDescriptions || [],
          vatRate: data.vatRate,
          settlementMode: data.settlementMode,
          quickEstimateSnapshot: data.quickEstimateSnapshot,
          isHighRisk: data.isHighRisk,
          auxiliaryMaterialsCost: data.auxiliaryMaterialsCost,
          riskBufferCost: data.riskBufferCost,
          laborItems: data.laborItems || [],
          deadline: data.deadline,
          marginOfError: data.marginOfError,
          area: data.area ?? data.estimatedAreaM2 ?? data.totalArea ?? data.projectArea ?? data.squareMeters ?? data.surfaceArea,
          estimatedAreaM2: data.estimatedAreaM2,
          totalArea: data.totalArea,
          projectArea: data.projectArea,
          squareMeters: data.squareMeters,
          surfaceArea: data.surfaceArea,
          voivodeship: data.voivodeship,
          status: data.status || 'draft',
          priceMode: data.priceMode,
          margin: data.margin,
          totalProfit: data.totalProfit,
          totalNetto: data.totalNetto,
          totalBrutto: data.totalBrutto,
          schedule: data.schedule || [],
          equipment: data.equipment || [],
          totalEquipmentCost: data.totalEquipmentCost || 0,
          clientToken: data.clientToken,
          clientAcceptedAt: data.clientAcceptedAt,
          clientPhone: data.clientPhone,
          sentAt: data.sentAt,
          progressPhotos: data.progressPhotos || [],
          beforeAfterPhotos: data.beforeAfterPhotos || [],
          missedItems: data.missedItems || [],
          risks: data.risks || [],
        });
      });
      setProjects(projectsData);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'projects');
    });

    return () => unsubscribe();
  }, [user, isAuthReady, userProfile?.role]);

  const login = async () => {
    console.log('DEBUG AppContext: Rozpoczynam login');
    await loginWithGoogle();
    console.log('DEBUG AppContext: loginWithGoogle zakończony');
  };

  const logoutUser = async () => {
    await logout();
  };

  const addDiscount = (discount: Omit<Discount, 'id'>) => {
    setDiscounts([...discounts, { ...discount, id: Date.now().toString() }]);
  };

  const removeDiscount = (id: string) => {
    setDiscounts(discounts.filter((d) => d.id !== id));
  };

  const addProject = async (projectData: Omit<Project, 'id' | 'userId' | 'createdAt' | 'status'>): Promise<string> => {
    if (!user) throw new Error("Musisz być zalogowany, aby dodać projekt.");
    
    
    if (
      R1_RELEASE_FLAGS.freeProjectLimit &&
      userProfile &&
      userProfile.status === 'trial' &&
      userProfile.projectCount >= 3
    ) {
    throw new Error("Osiągnięto limit 3 wycen w wersji próbnej. Skontaktuj się z nami w sprawie pełnej wersji.");
}

    try {
      const { runTransaction } = await import('firebase/firestore');
      
      const projectRef = doc(collection(db, 'projects'));
      await runTransaction(db, async (transaction) => {
        const profileRef = doc(db, 'user_profiles', user.uid);
        
        transaction.set(projectRef, {
          userId: user.uid,
          name: projectData.name,
          clientName: projectData.clientName || '',
          date: projectData.date,
          trade: projectData.trade,
          details: projectData.details,
          materials: projectData.materials,
          totalBaseCost: projectData.totalBaseCost,
          totalDiscountedCost: projectData.totalDiscountedCost,
          estimatedHours: projectData.estimatedHours || 0,
          tips: projectData.tips || [],
          laborCost: projectData.laborCost || 0,
          transportCost: projectData.transportCost || 0,
          location: projectData.location || '',
          lat: projectData.lat ?? null,
          lng: projectData.lng ?? null,
          images: projectData.images || [],
          vatRate: projectData.vatRate ?? 8,
          settlementMode: projectData.settlementMode ?? 'no_vat',
          quickEstimateSnapshot: projectData.quickEstimateSnapshot ?? null,
          isHighRisk: projectData.isHighRisk || false,
          auxiliaryMaterialsCost: projectData.auxiliaryMaterialsCost || 0,
          riskBufferCost: projectData.riskBufferCost || 0,
          laborItems: projectData.laborItems || [],
          deadline: projectData.deadline || '',
          status: 'draft',
          priceMode: projectData.priceMode || 'standard',
          margin: projectData.margin || 0,
          totalProfit: projectData.totalProfit || 0,
          totalNetto: projectData.totalNetto || 0,
          totalBrutto: projectData.totalBrutto || 0,
          area: projectData.area ?? 0,
          ...(projectData.estimatedAreaM2 !== undefined ? { estimatedAreaM2: projectData.estimatedAreaM2 } : {}),
          voivodeship: projectData.voivodeship || '',
          schedule: projectData.schedule || [],
          equipment: projectData.equipment || [],
          totalEquipmentCost: projectData.totalEquipmentCost || 0,
          createdAt: Date.now(),
          clientToken: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
          progressPhotos: [],
          beforeAfterPhotos: projectData.beforeAfterPhotos || [],
        });

        transaction.update(profileRef, {
          projectCount: (userProfile?.projectCount || 0) + 1
        });
      });

      // Update local profile state
      if (userProfile) {
        setUserProfile({
          ...userProfile,
          projectCount: userProfile.projectCount + 1
        });
      }
      return projectRef.id;
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'projects');
      throw error;
    }
  };

  const updateProject = async (id: string, projectData: QuickProjectUpdate<Project>): Promise<void> => {
    if (!user) throw new Error('Musisz być zalogowany, aby zapisać projekt.');
    if (!isUsableQuickProjectSnapshot(projectData.quickEstimateSnapshot)) {
      throw new Error('Nie można zapisać projektu: niepoprawny zapis szybkiej wyceny.');
    }
    try {
      const { updateDoc } = await import('firebase/firestore');
      await persistQuickProject({
        id,
        payload: projectData,
        isValid: (payload) => isUsableQuickProjectSnapshot(payload.quickEstimateSnapshot),
        update: async (projectId, payload) => updateDoc(doc(db, 'projects', projectId), {
          ...buildQuickProjectUpdate(payload),
          updatedAt: Date.now(),
        }),
        create: async () => {
          throw new Error('Nie można utworzyć projektu podczas aktualizacji.');
        },
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `projects/${id}`);
      throw error;
    }
  };

  const updateProjectStatus = async (id: string, status: Project['status']) => {
    try {
      const { updateDoc, doc } = await import('firebase/firestore');
      const updateData: any = { status };
      if (status === 'sent') {
        updateData.sentAt = Date.now();
      }
      await updateDoc(doc(db, 'projects', id), updateData);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'projects');
    }
  };

  const deleteProject = async (id: string) => {
    try {
      const { deleteDoc, doc } = await import('firebase/firestore');
      await deleteDoc(doc(db, 'projects', id));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'projects');
    }
  };

  const logError = async (error: string, projectDescription?: string) => {
    if (!user) return;
    try {
      await addDoc(collection(db, 'error_logs'), {
        userEmail: user.email,
        projectDescription: projectDescription || '',
        error,
        timestamp: Date.now(),
      });
    } catch (e) {
      console.error("Failed to log error to Firestore:", e);
    }
  };

  const sendFeedback = async (description: string, projectId?: string) => {
    if (!user) return;
    try {
      await addDoc(collection(db, 'feedback'), {
        userEmail: user.email,
        description,
        projectId: projectId || '',
        timestamp: Date.now(),
      });
    } catch (e) {
      handleFirestoreError(e, OperationType.CREATE, 'feedback');
    }
  };

  const updateUserProfile = async (data: Partial<UserProfile>) => {
    if (!user) return;
    try {
      const { updateDoc, doc } = await import('firebase/firestore');
      const updateData: any = { ...data };
      await updateDoc(doc(db, 'user_profiles', user.uid), updateData);
      setUserProfile(prev => prev ? { ...prev, ...data } : null);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'user_profiles');
    }
  };

  const addProgressPhoto = async (projectId: string, photo: { url: string; comment: string }) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    
    const newPhotos = [...(project.progressPhotos || []), { ...photo, timestamp: Date.now() }];

    try {
      const { updateDoc, doc } = await import('firebase/firestore');
      await updateDoc(doc(db, 'projects', projectId), {
        progressPhotos: newPhotos
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'projects');
    }
  };

  const generateAIPhotoComment = async (_imageUrl: string, context: string) => {
    const trimmedContext = context.trim();
    return trimmedContext
      ? `Zdjęcie dodane do dokumentacji: ${trimmedContext}.`
      : 'Zdjęcie dodane do dokumentacji.';
  };

  // Old /quote links must not query private projects or bypass offer authorization.
  const getProjectByToken = async (_token: string): Promise<Project | null> => null;
  const acceptProjectAsClient = async (_projectId: string, _token: string): Promise<void> => {
    throw new Error('Ten link wygasł. Poproś wykonawcę o ponowne udostępnienie oferty.');
  };
  const addToGoogleCalendar = async (event: { summary: string; location: string; description: string; start: string; end: string }) => {
    if (!AI_API_URL) {
      throw new Error("Kalendarz chwilowo niedostępny. Zapisz termin w projekcie.");
    }

    const startDate = new Date(event.start);
    const endDate = new Date(event.end);
    const response = await fetch(buildApiUrl('/api/calendar/create-event'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        projectId: `calendar-${event.summary.toLowerCase().replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || Date.now()}`,
        title: event.summary,
        address: event.location,
        date: startDate.toISOString().split('T')[0],
        startTime: startDate.toTimeString().slice(0, 5),
        endTime: endDate.toTimeString().slice(0, 5),
        note: event.description,
      })
    });
    const payload = await response.json().catch(() => null);

    if (!response.ok || payload?.ok === false) {
      throw new Error(payload?.error === 'calendar_not_configured'
        ? "Kalendarz chwilowo niedostępny. Zapisz termin w projekcie."
        : "Błąd podczas dodawania do kalendarza.");
    }
    return;

    const token = localStorage.getItem('gcal_token');
    if (!token) {
      throw new Error("Brak uprawnień do kalendarza. Zaloguj się ponownie.");
    }

    const legacyResponse = await fetch(buildApiUrl('/api/calendar/create-event'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        summary: event.summary,
        location: event.location,
        description: event.description,
        start: {
          dateTime: event.start,
          timeZone: 'Europe/Warsaw'
        },
        end: {
          dateTime: event.end,
          timeZone: 'Europe/Warsaw'
        }
      })
    });

    if (!legacyResponse.ok) {
      const errorData = await legacyResponse.json();
      if (errorData.error?.code === 401) {
        localStorage.removeItem('gcal_token');
        throw new Error("Sesja kalendarza wygasła. Zaloguj się ponownie.");
      }
      throw new Error(errorData.error?.message || "Błąd podczas dodawania do kalendarza.");
    }
  };

  return (
    <AppContext.Provider value={{ 
      user, isAuthReady, isAllowed, userProfile, marketPrices, login, logoutUser, 
      discounts, addDiscount, removeDiscount, projects, addProject, updateProject,
      updateProjectStatus, deleteProject, logError, sendFeedback, updateUserProfile, 
      addProgressPhoto, generateAIPhotoComment, getProjectByToken, acceptProjectAsClient,
      addToGoogleCalendar
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
